/**
 * Supabase client initialization.
 *
 * Important: This project uses react-scripts (CRA). All environment variables
 * MUST be prefixed with REACT_APP_ to be included in the production build.
 */
import { createClient } from '@supabase/supabase-js';

// react-scripts (CRA) uses process.env and REACT_APP_ prefix
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL ||
    (process.env.REACT_APP_API_URL?.includes('supabase.co') ? process.env.REACT_APP_API_URL : null) ||
    process.env.VITE_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
    console.error('Supabase configuration error:');
    if (!supabaseUrl) console.error('- REACT_APP_SUPABASE_URL is missing. (Note: REACT_APP_API_URL is set to:', process.env.REACT_APP_API_URL, ')');
    if (!supabaseAnonKey) console.error('- REACT_APP_SUPABASE_ANON_KEY is missing.');
}

const authLocks = new Map();

// The default lock is navigator.locks. The session refresh tick requests it
// with a zero timeout, and Chrome reports that miss as an uncaught rejection
// while getSession still holds the lock. That race happens on every page.
// Queue auth work in this document instead, and let the tick skip a busy turn.
const authLock = async (name, acquireTimeout, fn) => {
    const slot = authLocks.get(name) || { pending: 0, tail: Promise.resolve() };
    authLocks.set(name, slot);
    if (acquireTimeout === 0 && slot.pending > 0) return undefined;

    slot.pending += 1;
    const run = slot.tail.then(fn, fn);
    slot.tail = run.then(() => undefined, () => undefined);
    try {
        return await run;
    } finally {
        slot.pending -= 1;
        if (slot.pending === 0) authLocks.delete(name);
    }
};

const authOptionsForDocument = (inFrame) => ({
    auth: {
        lock: authLock,
        ...(inFrame ? { autoRefreshToken: false, detectSessionInUrl: false } : {}),
    },
});

const inFrame = typeof window !== 'undefined' && window.parent !== window;

export const supabase = createClient(
    supabaseUrl || '',
    supabaseAnonKey || '',
    authOptionsForDocument(inFrame),
);
